import { makeStyles } from '@material-ui/core'

// Column ratios are shared by tableHead, tableRow and the skeleton so the three stay aligned.
// A skeleton built on a different grid causes a layout jump the moment data arrives.
//
// `minmax(0, Nfr)` rather than a bare `Nfr`, and this is the whole reason the rows were
// misaligned. A bare `Nfr` track is `minmax(auto, Nfr)` — its FLOOR is min-content, so any
// cell holding an unbreakable string is allowed to push its track past the share the ratio
// asks for. A provider code (`prv_3jlMarXac3dDuXR334dc84Njr…`) has no break opportunity, so
// the Provider track grew to fit the longest code on the page and shoved Status, Discount
// and Updated out of their columns — and because each row sized independently, no two rows
// agreed on where a column started. `minmax(0, …)` sets the floor to zero, so the ratio is
// obeyed and the overflow is handled by the cell (ellipsis) instead of the track.
//
// The LAST track stays `auto`, deliberately — it holds the row's action menu, not text.
// An earlier version of this fix applied `minmax(0, …)` uniformly, which removed the
// min-content floor from the actions column too: at 1fr of 34 total it is ~3% of the
// grid, so the icon button had nothing left holding it above a tappable size. `auto`
// sizes that track to its content, which is what a control column wants. The zero floor
// is the right answer for TEXT tracks and the wrong one for a control track.
const COLUMNS =
  'minmax(0, 10fr) minmax(0, 6fr) minmax(0, 5fr) minmax(0, 6fr) minmax(0, 6fr) auto'
const COLUMNS_MOBILE = 'minmax(0, 9fr) minmax(0, 5fr) minmax(0, 4fr) auto'

const useStyles = makeStyles((theme) => ({
  root: {},
  tableHead: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: COLUMNS,
    color: theme.palette.text.secondary,
    alignItems: 'center',
    padding: '0 0 0 12px',
    marginBottom: 4,
    marginTop: 4,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12
  },
  tableHeadMobile: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: COLUMNS_MOBILE,
    color: theme.palette.text.secondary,
    alignItems: 'center',
    padding: '0 0 0 12px',
    marginBottom: 4,
    marginTop: 4,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: COLUMNS,
    height: 100,
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: 20,
    cursor: 'pointer',
    paddingLeft: 12,
    transition: 'background-color 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.background.paper
    }
  },
  tableRowMobile: {
    display: 'grid',
    columnGap: 8,
    gridTemplateColumns: COLUMNS_MOBILE,
    height: 100,
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: 20,
    cursor: 'pointer',
    paddingLeft: 12,
    transition: 'background-color 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.background.paper
    }
  },
  // `minWidth: 0` because this is a FLEX container, and a flex item defaults to
  // `min-width: auto` — its own min-content floor, independent of the track. So
  // `minmax(0, 10fr)` on the track alone does not protect this cell: an unbreakable token
  // in a promotion name (a URL-like slug, a long compound, a no-space Persian string)
  // reproduces the exact provider-code defect on the name column. Lower likelihood than
  // the provider code, which was ALWAYS an unbreakable 60-char token — but the same
  // mechanism, and it would have been left as the one at-risk cell in the row that was
  // not fixed. Shared by PromotionRow and PromotionRowMobile, so both are covered.
  nameCell: {
    display: 'flex',
    minWidth: 0,
    // `overflow: hidden` as well as the floor. `minWidth: 0` lets the ITEM shrink, but
    // the text inside still paints outside it — and the row has a fixed `height: 100`,
    // so a wrapping name overflows vertically into the row below as readily as an
    // unbreakable one overflows sideways into Provider. Names are operator-entered free
    // text, so both are reachable.
    overflow: 'hidden',
    paddingRight: 12
  },
  // The other half of the fix above. `minmax(0, …)` stops the TRACK growing; without a
  // clip the text still paints over the neighbouring column. `minWidth: 0` is repeated
  // here because a grid ITEM also defaults to a min-content floor — setting it on the
  // track alone is not enough when the item is itself a flex or grid container.
  ellipsisCell: {
    minWidth: 0,
    paddingRight: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  refreshButtonContainer: {
    margin: 'auto'
  },
  noDataImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 62
  },
  skeleton: {
    width: '80%'
  },
  iconSize: {
    fontSize: 42
  }
}))

export default useStyles
